import React, { useCallback, useState } from 'react';
import type { RemirrorEventListener } from 'remirror';
import type { Remirror as RemirrorTypes } from '@trinity/chat';
import type { WidgetConfig, WidgetDataSource } from '@trinity/chat/types/dashboard-config';
import { useDashboardBuilderEditing } from '../../../containers/Dashboard/context.js';
import { BasicMarkupEditor, type BasicMarkupEditorExtensions } from '../../Remirror/BasicMarkupEditor.js';
import { useBuilderPalette } from './PaletteContext.js';

// Rich text edited in place on the canvas (no sidebar step) — used by builders whose palette sets
// `textEditing: 'inline'` (the PJ media library) and by the PJ grid / stage view cells.

/** Config for a new inline rich-text widget. */
export const INLINE_TEXT_CONFIG = { title: '', textMode: 'richtext' } as const;

/** Rich text is edited in place; a markdown text widget keeps the sidebar form. */
export function isInlineText(widget: Pick<WidgetConfig, 'type' | 'config'>): boolean {
  return widget.type === 'text' && (widget.config as { textMode?: string }).textMode !== 'markdown';
}

export function textDataSource(content: string): WidgetDataSource {
  return { type: 'static', data: { type: 'text', data: { content } } };
}

/** True when this builder edits rich text in place instead of in the sidebar. */
export function useInlineTextEditing(): boolean {
  return useBuilderPalette()?.textEditing === 'inline';
}

function readTextContent(widget: WidgetConfig): RemirrorTypes.RootNode | null {
  const source = widget.dataSource as { type?: string; data?: { data?: { content?: unknown } } } | undefined;
  const content = source?.type === 'static' ? source.data?.data?.content : undefined;
  if (typeof content !== 'string' || !content) return null;
  try {
    return JSON.parse(content) as RemirrorTypes.RootNode;
  } catch {
    return null;
  }
}

/** In-place rich text editor; each change is written to the widget's stored content. */
export function InlineTextEditor({ widget }: { widget: WidgetConfig }): React.ReactElement {
  const { updateWidgetDataSource } = useDashboardBuilderEditing();
  // The editor reads its content once; keep the initial value stable across re-renders.
  const [initialContent] = useState(() => readTextContent(widget));
  const handleChange: RemirrorEventListener<BasicMarkupEditorExtensions> = useCallback(
    (params) => {
      if (!params.tr?.docChanged) return;
      updateWidgetDataSource(widget.id, textDataSource(JSON.stringify(params.state.doc.toJSON())));
    },
    [updateWidgetDataSource, widget.id],
  );

  return <BasicMarkupEditor editing content={initialContent} onChange={handleChange} imageToolbarOptions={{ videos: false }} />;
}
